<template>
  <DetailCard
    v-if="!isLoading"
    key="urun-gorselleri"
    title="Ürün Görselleri"
    cardKey="product-images"
    :showUpdateButton="true"
    @requestUpdate="handleRequestUpdate"
    :isLoading="isLoading"
  >
    <div class="images-wrapper" :class="{ disabled: isProductDisabled }">
      <button 
        v-if="showLeftArrow && localImages.length > 8" 
        class="scroll-btn left" 
        :disabled="isProductDisabled"
        @click="scrollImages(-1)"
      >
        <img src="@/assets/images/icons-arrow-1-right.svg" alt="Sol" />
      </button>
      <div ref="imagesGridRef" class="images-grid" :class="{ disabled: isProductDisabled }" @scroll="onScroll">
        <div
          class="image-thumb"
          v-for="(img, idx) in localImages"
          :key="idx"
          :class="{ cover: img.isCover }"
        >
          <img
            v-if="!needsPlaceholder(img, idx)"
            :src="img?.src"
            alt="ürün görseli"
            @error="onImgError(idx)"
          />

          <img
            v-else
            class="isPlaceholder"
            src="@/assets/images/icons/image-placeholder.svg"
            alt="ürün görseli"
          />
          <div v-if="img?.isCover" class="cover-label">Kapak Görseli</div>
        </div>
      </div>
      <button 
        v-if="localImages.length > 8" 
        class="scroll-btn right" 
        :disabled="isProductDisabled"
        @click="scrollImages(1)"
      >
        <img src="@/assets/images/icons-arrow-1-right.svg" alt="Sağ" />
      </button>
    </div>

    <div class="info-message" :class="{ disabled: isProductDisabled }" v-if="!isLoading">
      <Icons name="InfoCircle" :color="isProductDisabled ? '#C7C7C7' : '#4FA4E5'" lg class="info-icon" />
      <span class="info-text">
        Görsellerde eksik/hata olduğunu düşünüyorsanız
        <span 
          class="highlight-text" 
          @click="handleRequestUpdate"
          :class="{ disabled: isProductDisabled }"
        >
          Güncelleme Talep Et/Hata Bildir
        </span>
        sayfasından düzenleme talep edebilirsiniz.
      </span>
    </div>
  </DetailCard>
  <ProductImageSkeleton v-else></ProductImageSkeleton>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import DetailCard from "./DetailCard.vue";
import { Icons } from "@n11/so-ui-components";
import ProductImageSkeleton from '@/components/Skeleton/ProductDetailSkeleton/ProductImageSkeleton.vue';

const store = useStore();

const props = defineProps({
  images: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['requestUpdate']);

const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const localImages = computed(() => {
  if (!props.images || props.images.length === 0) {
    return [];
  }

  return props.images;
});

const imagesGridRef = ref(null);
const showLeftArrow = ref(false);
const brokenIndexes = ref([]);

const needsPlaceholder = (img, idx) => {
  if (!img?.src) return true;
  return brokenIndexes.value.includes(idx);
};

const onImgError = (idx) => {
  if (!brokenIndexes.value.includes(idx)) {
    brokenIndexes.value.push(idx);
  }
};


const scrollImages = (direction) => {
  if (!imagesGridRef.value) return;
  
  const scrollAmount = 112;
  imagesGridRef.value.scrollBy({
    left: direction * scrollAmount,
    behavior: 'smooth'
  });
};

const onScroll = () => {
  if (!imagesGridRef.value) return;
  showLeftArrow.value = imagesGridRef.value.scrollLeft > 0;
};

const handleRequestUpdate = () => {
  if (isProductDisabled.value) return;
  emit('requestUpdate');
};
watch(
  () => props.images,
  () => { brokenIndexes.value = []; }
);
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.images-wrapper {
  position: relative;
  width: 100%;
  max-width: 984px;

  &.disabled {
    filter: grayscale(100%);
  }
}

.images-grid {
  width: 100%;
  display: flex;
  gap: 12px;
  padding: 12px 0 18px;
  overflow-x: auto;
  flex-wrap: nowrap;
  scroll-behavior: smooth;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.scroll-btn {
  position: absolute;
  top: 35px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: $colorN00;
  border: 1px solid $colorD20;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &.left {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.20);
    left: 5px;
    width: 40px;
    height: 40px;
    
    img {
      transform: scaleX(-1);
    }
  }

  &.right {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.20);
    width: 40px;
    height: 40px;
    right: 5px;
  }

  img {
    width: 25px;
    height: 25px;
  }

  &:hover:not(:disabled) {
    background: $colorN10;
  }

  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
}

.images-grid.disabled {
  pointer-events: none;
  .image-thumb {
    opacity: 0.6;
  }
}

.image-thumb {
  width: 100px;
  height: 100px;
  min-width: 100px;
  min-height: 100px;
  max-width: 100px;
  max-height: 100px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid $colorD20;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $colorN00;
}

.image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}
.image-thumb img.isPlaceholder {
  height: auto; 
  width: 100%;
  object-fit: cover;
}
.cover-label {
  position: absolute;
  top: 76px;
  width: 100%;
  text-align: center;
  background: $colorN80;
  color: $colorN00;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 14px;
  padding: 4px 10px;
}

.skeleton-grid {
  display: flex;
  gap: 12px;
  padding: 12px 0 18px;
}

.skeleton-thumb {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  background-color: $gray-19;
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
/* --- --- --- */


@media (max-width: 768px) {
  .images-grid,
  .skeleton-grid {
    flex-wrap: nowrap;
  }

  .skeleton-thumb {
    width: 80px;
    height: 80px;
  }
}

.info-message {
  display: flex;
  gap: 8px;
  padding-top: 16px;
  .info-text {
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.05px;
    color: #242424;
    .highlight-text {
      color: $colorM50;
      font-weight: 600;
      cursor: pointer;
    }
  }

  &.disabled {
    .info-text {
      color: $colorN60;
      .highlight-text {
        color: $colorN50;
      }
    }
  }
}
</style>
